// useWhatsAppSipConnection.js
/* eslint-disable no-console, no-use-before-define, no-underscore-dangle, default-case, prettier/prettier */
import { ref, computed, markRaw, onUnmounted } from 'vue';
import WhatsAppSipClient from './WhatsAppSipClient';
import OutboundCallsAPI from 'dashboard/api/whatsappSip/outboundCalls';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  notifyRegistrationEvent,
  notifyCallEvent,
} from 'dashboard/composables/useSipStatusToast';

const TOAST_LABEL = 'WhatsApp SIP';

// Singleton state - stored on window to survive HMR (Vite hot module replacement).
// Without this, HMR creates a new module with new refs, but the old WhatsAppSipClient
// instance still holds callbacks to the old refs, causing state desync.
function getHmrSafeState() {
  if (!window.__whatsappSipState) {
    window.__whatsappSipState = {
      client: ref(null),
      isRegistered: ref(false),
      isConnecting: ref(false),
      connectionError: ref(null),
      callState: ref('idle'),
      callDirection: ref(null),
      callNumber: ref(null),
      remoteDisplayName: ref(null),
      callStartTime: ref(null),
      isMuted: ref(false),
      isOnHold: ref(false),
      isConsultationActive: ref(false),
      consultationState: ref('idle'),
      consultationTarget: ref(null),
      currentSession: null,
      lastCallEndedAt: 0,
      outboundCallEventId: null,
      outboundCallStartedAt: null,
      callerExtension: null,
    };
  }
  return window.__whatsappSipState;
}

const _state = getHmrSafeState();

const client = _state.client;
const isRegistered = _state.isRegistered;
const isConnecting = _state.isConnecting;
const connectionError = _state.connectionError;

// Call state
const callState = _state.callState;
const callDirection = _state.callDirection;
const callNumber = _state.callNumber;
const remoteDisplayName = _state.remoteDisplayName;
const callStartTime = _state.callStartTime;
const isMuted = _state.isMuted;
const isOnHold = _state.isOnHold;

// Consultation (attended transfer) state
const isConsultationActive = _state.isConsultationActive;
const consultationState = _state.consultationState;
const consultationTarget = _state.consultationTarget;

// Cooldown after call ends (prevents ghost re-dial from Vue re-render)
const MAKE_CALL_COOLDOWN_MS = 1500;

export function useWhatsAppSipConnection() {
  /**
   * Connect to the WhatsApp SIP server.
   * Credentials are passed directly — no API call needed.
   * @param {{ extension: string, password: string, displayName: string }} credentials
   */
  async function connect(credentials = {}) {
    const TAG = '[WhatsAppSIP]';

    if (client.value) {
      console.log(TAG, 'connect() skipped: client already exists, isRegistered=', isRegistered.value);
      return true;
    }
    if (isConnecting.value) {
      console.log(TAG, 'connect() skipped: already connecting');
      return true;
    }

    const { extension, password, displayName, sipServer, wsServer } = credentials;

    if (!extension || !password) {
      console.error(TAG, 'connect() FAILED: missing credentials', { extension: !!extension, password: !!password });
      connectionError.value = 'Extension and password are required';
      return false;
    }

    _state.callerExtension = extension;
    isConnecting.value = true;
    connectionError.value = null;

    console.log(TAG, 'connect() START', {
      extension,
      displayName,
      sipServer: sipServer || '(default)',
      wsServer: wsServer || '(default)',
    });

    try {
      const clientOpts = {
        extension,
        password,
        displayName: displayName || extension,
        debug: false,
        onUserAgentAction: handleUserAgentAction,
        onCallEvent: handleCallEvent,
      };
      if (sipServer) clientOpts.sipServer = sipServer;
      if (wsServer) clientOpts.wsServer = wsServer;

      console.log(TAG, 'Creating WhatsAppSipClient...');
      client.value = markRaw(new WhatsAppSipClient(clientOpts));

      console.log(TAG, 'Calling client.start() — opening WebSocket to Asterisk...');
      await client.value.start();
      console.log(TAG, 'client.start() completed — waiting for SIP REGISTER response');
      return true;
    } catch (err) {
      console.error(TAG, 'connect() EXCEPTION:', err.message);
      console.error(TAG, 'Full error:', err);
      connectionError.value = err.message;
      isConnecting.value = false;
      client.value = null;
      return false;
    }
  }

  /**
   * Disconnect from the SIP server
   */
  function disconnect() {
    if (client.value) {
      client.value.stop();
      client.value = null;
    }
    isRegistered.value = false;
    isConnecting.value = false;
    resetCallState();
  }

  /**
   * Handle user agent events (registration, etc)
   */
  function handleUserAgentAction(event) {
    const TAG = '[WhatsAppSIP]';

    // Toast visual do ciclo de registro (Sonner em bottom-right) —
    // helper gerencia o toastId pra que o mesmo balão transicione
    // loading → success/error em vez de empilhar 3 toasts.
    notifyRegistrationEvent(event, TOAST_LABEL);

    switch (event.type) {
      case 'connected':
        console.log(TAG, 'EVENT: WebSocket CONNECTED to Asterisk');
        break;

      case 'registered':
        console.log(TAG, 'EVENT: SIP REGISTERED — endpoint is reachable by Asterisk');
        isRegistered.value = true;
        isConnecting.value = false;
        break;

      case 'unregistered':
        console.warn(TAG, 'EVENT: SIP UNREGISTERED — endpoint removed from Asterisk');
        isRegistered.value = false;
        isConnecting.value = false;
        break;

      case 'registrationFailed':
        console.error(TAG, 'EVENT: SIP REGISTRATION FAILED', event.payload || '');
        console.error(TAG, 'Possíveis causas: senha errada, endpoint não existe no Asterisk, WebSocket URL incorreta');
        isRegistered.value = false;
        isConnecting.value = false;
        connectionError.value = 'SIP registration failed';
        break;

      case 'disconnected':
        console.warn(TAG, 'EVENT: WebSocket DISCONNECTED from Asterisk', event.payload || '');
        console.warn(TAG, 'isRegistered será false — chamadas não chegarão a este ramal');
        isRegistered.value = false;
        isConnecting.value = false;
        break;

      case 'newRTCSession':
        handleNewRTCSession(event.payload);
        break;

      default:
        console.log(TAG, 'EVENT:', event.type, event.payload || '');
    }
  }

  /**
   * Handle new RTC session (incoming/outgoing call)
   */
  function handleNewRTCSession(rtcPayload) {
    const { session } = rtcPayload;

    if (!session) {
      console.error('[useWhatsAppSipConnection] Invalid rtcPayload');
      return;
    }

    _state.currentSession = session;
    callDirection.value = session.direction;

    if (session.direction === 'incoming') {
      const callerNumber = session.remote_identity?.uri?.user || 'unknown';
      const callerDisplayName = session.remote_identity?.display_name || null;

      callState.value = 'ringing';
      callNumber.value = callerNumber;
      remoteDisplayName.value = callerDisplayName;
    } else {
      callState.value = 'calling';
      callNumber.value = client.value?.currentDestination || 'unknown';
      remoteDisplayName.value = null;
    }
  }

  /**
   * Make an outgoing call
   * @param {string} number - Number to call
   * @param {function} onCallAction - Callback for call events
   */
  function makeCall(number, onCallAction, inboxId) {
    if (Date.now() - _state.lastCallEndedAt < MAKE_CALL_COOLDOWN_MS) {
      return;
    }

    if (!client.value || !isRegistered.value) {
      throw new Error('WhatsApp SIP not connected');
    }

    if (!number) {
      throw new Error('Number is required');
    }

    const cleanNumber = String(number).replace(/[^\d+]/g, '');

    if (!cleanNumber) {
      throw new Error('Invalid number');
    }

    callState.value = 'calling';
    callDirection.value = 'outgoing';
    callNumber.value = cleanNumber;
    // Guarda o inbox da ligacao pro tracking (trackOutboundStart) registrar de
    // qual caixa de entrada/numero a chamada saiu.
    _state.outboundInboxId = inboxId || null;

    client.value.call({
      to: cleanNumber,
      inboxId,
      // NÃO chamar handleCallEvent aqui — o client já o invoca via
      // params.onCallEvent dentro de emitCallEvent. Chamar nos dois lugares
      // disparava cada evento 2x (toasts duplicados). Aqui só repassamos pro
      // callback externo de quem chamou makeCall (ex.: painel).
      onCallAction: event => {
        if (onCallAction) onCallAction(event);
      },
    });
  }

  function trackOutboundStart() {
    if (_state.outboundCallEventId) return;
    if (_state._trackingInFlight) return;
    _state._trackingInFlight = true;
    _state.outboundCallStartedAt = Date.now();
    const ext = _state.callerExtension || '';
    const extNumber = ext.includes('-') ? ext.split('-').pop() : ext;
    OutboundCallsAPI.create({
      caller_extension: extNumber,
      called_number: callNumber.value,
      inbox_id: _state.outboundInboxId || undefined,
    })
      .then(({ data }) => {
        _state.outboundCallEventId = data.id;
      })
      .catch(err => {
        console.warn('[WhatsAppSIP] outbound call tracking start failed', err);
      })
      .finally(() => {
        _state._trackingInFlight = false;
      });
  }

  function trackOutboundEnd(disposition) {
    const eventId = _state.outboundCallEventId;
    if (!eventId) return;
    const wasAnswered = callStartTime.value !== null;
    const talkTime = wasAnswered
      ? Math.round((Date.now() - callStartTime.value.getTime()) / 1000)
      : 0;
    const finalDisposition = wasAnswered ? 'answered' : disposition;
    _state.outboundCallEventId = null;
    _state.outboundCallStartedAt = null;
    OutboundCallsAPI.complete(eventId, {
      disposition: finalDisposition,
      talk_time: talkTime,
      answered: wasAnswered,
    }).catch(err => {
      console.warn('[WhatsAppSIP] outbound call tracking end failed', err);
    });
  }

  /**
   * Handle call events
   */
  function handleCallEvent(event) {
    // Toast da chamada em andamento (loading → success/error no mesmo balão).
    // Só pra outgoing — incoming já tem ringtone + UI dedicada no dialer.
    // `rejected` é exceção: o onReject do SIP.js dispara DEPOIS do Terminated,
    // quando o `ended` já resetou callDirection pra null. Como só emitimos
    // `rejected` em chamadas de saída, deixamos passar sempre.
    if (callDirection.value === 'outgoing' || event.type === 'rejected') {
      notifyCallEvent(event, `${TOAST_LABEL}: chamada`);
    }

    switch (event.type) {
      case 'incoming':
        // Already handled in handleNewRTCSession
        break;

      case 'progress':
        if (callDirection.value === 'outgoing') {
          callState.value = 'ringing';
          trackOutboundStart();
        }
        break;

      case 'accepted':
      case 'confirmed':
        callState.value = 'connected';
        if (!callStartTime.value) {
          callStartTime.value = new Date();
        }
        break;

      case 'ended':
        trackOutboundEnd('answered');
        resetCallState();
        emitter.emit(BUS_EVENTS.WHATSAPP_SIP_INCOMING_CALL, { ended: true });
        break;

      case 'failed':
        trackOutboundEnd('failed');
        resetCallState();
        break;

      case 'hold':
        isOnHold.value = true;
        break;

      case 'unhold':
        isOnHold.value = false;
        break;

      // Consultation (attended transfer) events
      case 'consultation_calling':
        isConsultationActive.value = true;
        consultationState.value = 'calling';
        consultationTarget.value = event.target || null;
        break;

      case 'consultation_progress':
        consultationState.value = 'ringing';
        break;

      case 'consultation_established':
        consultationState.value = 'connected';
        break;

      case 'consultation_ended':
        resetConsultationState();
        break;

      case 'consultation_failed':
        resetConsultationState();
        break;

      case 'consultation_cancelled':
        resetConsultationState();
        break;

      case 'attended_transfer_completed':
        resetConsultationState();
        break;

      case 'attended_transfer_failed':
        console.error('[WhatsAppSIP] Attended transfer failed:', event.error);
        break;
    }
  }

  /**
   * Answer an incoming call
   */
  function answerCall() {
    if (!client.value) return;

    if (callState.value !== 'ringing') {
      console.warn('[useWhatsAppSipConnection] Call is not ringing, current state:', callState.value);
      return;
    }

    callState.value = 'connecting';
    client.value.answer();
  }

  /**
   * Hang up the current call
   */
  function hangup() {
    if (!client.value) return;

    try {
      client.value.hangup();

      if (callState.value === 'ringing' && callDirection.value === 'incoming') {
        resetCallState();
      }
    } catch (e) {
      console.error('[useWhatsAppSipConnection] Hangup error:', e);
      resetCallState();
    }
  }

  /**
   * Toggle microphone mute
   */
  function toggleMute() {
    if (!client.value) return false;
    const muted = client.value.toggleMute();
    isMuted.value = muted;
    return muted;
  }

  /**
   * Toggle hold state
   */
  function toggleHold() {
    if (!client.value) return false;
    try {
      const held = client.value.toggleHold();
      isOnHold.value = held;
      return held;
    } catch (e) {
      console.error('[WhatsAppSIP] Toggle hold error:', e.message);
      return isOnHold.value;
    }
  }

  /**
   * Transfer call to another number/extension (blind)
   */
  function transfer(target) {
    if (!client.value) {
      throw new Error('WhatsApp SIP not connected');
    }
    const cleanTarget = String(target).replace(/[^\d+]/g, '');
    if (!cleanTarget) {
      throw new Error('Invalid transfer target');
    }
    client.value.transfer(cleanTarget);
  }

  /**
   * Start attended (consultative) transfer
   */
  function attendedTransfer(target) {
    if (!client.value) {
      throw new Error('WhatsApp SIP not connected');
    }
    const cleanTarget = String(target).replace(/[^\d+]/g, '');
    if (!cleanTarget) {
      throw new Error('Invalid transfer target');
    }
    client.value.attendedTransfer(cleanTarget);
  }

  /**
   * Complete the attended transfer
   */
  function completeAttendedTransfer() {
    if (!client.value) {
      throw new Error('WhatsApp SIP not connected');
    }
    client.value.completeAttendedTransfer();
  }

  /**
   * Cancel the attended transfer and return to the original call
   */
  function cancelAttendedTransfer() {
    if (!client.value) {
      throw new Error('WhatsApp SIP not connected');
    }
    client.value.cancelAttendedTransfer();
  }

  /**
   * Reset consultation state
   */
  function resetConsultationState() {
    isConsultationActive.value = false;
    consultationState.value = 'idle';
    consultationTarget.value = null;
  }

  /**
   * Send DTMF tone
   */
  function sendDTMF(tone) {
    if (!client.value) return;
    client.value.sendDTMF(tone);
  }

  /**
   * Reset call state
   */
  function resetCallState() {
    _state.lastCallEndedAt = Date.now();
    callState.value = 'idle';
    callDirection.value = null;
    callNumber.value = null;
    remoteDisplayName.value = null;
    callStartTime.value = null;
    isMuted.value = false;
    isOnHold.value = false;
    resetConsultationState();
    _state.currentSession = null;
  }

  /**
   * Get call duration in seconds
   */
  function getCallDuration() {
    if (!callStartTime.value) return 0;
    return Math.floor((new Date() - callStartTime.value) / 1000);
  }

  /**
   * Format duration as MM:SS
   */
  function formatDuration(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  // Computed properties
  const isConnected = computed(() => isRegistered.value);
  const hasActiveCall = computed(() =>
    ['calling', 'ringing', 'connecting', 'connected'].includes(callState.value)
  );
  const isInCall = computed(() => callState.value === 'connected');

  // Cleanup on unmount — keep connection alive across components
  onUnmounted(() => {});

  // HMR: if client already exists, update its callbacks to reference the current module's functions.
  if (client.value) {
    client.value.onUserAgentAction = handleUserAgentAction;
    client.value.params.onCallEvent = handleCallEvent;
  }

  return {
    // State
    client,
    isRegistered,
    isConnecting,
    isConnected,
    connectionError,

    // Call state
    callState,
    callDirection,
    callNumber,
    remoteDisplayName,
    callStartTime,
    isMuted,
    isOnHold,
    hasActiveCall,
    isInCall,

    // Consultation (attended transfer) state
    isConsultationActive,
    consultationState,
    consultationTarget,

    // Methods
    connect,
    disconnect,
    makeCall,
    answerCall,
    hangup,
    toggleMute,
    toggleHold,
    transfer,
    attendedTransfer,
    completeAttendedTransfer,
    cancelAttendedTransfer,
    sendDTMF,
    getCallDuration,
    formatDuration,

    // Audio unlock (must be called from user interaction)
    unlockAudio: () => client.value?.unlockAudio(),
  };
}
